import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateBreadcrumbSchema, generateArticleSchema } from "@/lib/jsonld";

// Images
import coverImage from "@/assets/blog/outil-fidelisation-petit-commerce/couverture.webp";
import grandeEnseigneImage from "@/assets/blog/outil-fidelisation-petit-commerce/grande-enseigne-vs-petit-commerce.webp";
import walletQrCodeImage from "@/assets/blog/outil-fidelisation-petit-commerce/ajout-carte-wallet-qr-code.webp";
import tamponProgressionImage from "@/assets/blog/outil-fidelisation-petit-commerce/programme-fidelite-tampon-progression.webp";
import erreursImage from "@/assets/blog/outil-fidelisation-petit-commerce/erreurs-programme-fidelite.webp";

const OutilFidelisationPetitCommerce = () => {
  const jsonLdSchemas = [
    generateWebPageSchema(
      "Outil de fidélisation pour petit commerce : par où commencer sans gros budget",
      seoConfig.blogOutilFidelisationPetitCommerce.description,
      "/outil-fidelisation-petit-commerce"
    ),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: "Outil de fidélisation petit commerce", path: "/outil-fidelisation-petit-commerce" }
    ]),
    generateArticleSchema(
      "Outil de fidélisation pour petit commerce : par où commencer sans gros budget",
      seoConfig.blogOutilFidelisationPetitCommerce.description,
      "https://unotif.com/outil-fidelisation-petit-commerce",
      "2026-03-26"
    )
  ];

  const imageStyle = {
    boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO {...seoConfig.blogOutilFidelisationPetitCommerce} jsonLd={jsonLdSchemas} />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="pt-24 lg:pt-40 pb-12 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav className="text-sm font-medium text-[#495456] mb-6">
              <Link to="/blog" className="text-[#0c97a9] hover:underline">Blog</Link>
              <span className="mx-2">›</span>
              <span className="text-[#074155]">Outil de fidélisation petit commerce</span>
            </nav>

            {/* H1 */}
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-8">
              Outil de fidélisation pour petit commerce : par où commencer sans gros budget
            </h1>

            {/* Cover Image */}
            <img
              src={coverImage}
              alt="Illustration des outils de fidélisation pour petit commerce avec solution simple et accessible sans gros budget"
              loading="eager"
              className="rounded-2xl w-full h-auto"
              style={imageStyle}
            />
          </div>
        </section>

        {/* Article Content */}
        <section className="pb-20 sm:pb-28 lg:pb-36">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Introduction */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les grandes enseignes ont des équipes marketing et des budgets à six chiffres pour fidéliser leurs clients. Vous, non. Et pourtant, votre avantage est réel : vous connaissez vos clients par leur prénom, vous construisez une relation de proximité impossible à reproduire à grande échelle.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La question n'est pas de savoir si vous devez fidéliser vos clients : c'est évident. La question est de savoir quel <strong className="font-semibold">outil de fidélisation</strong> utiliser sans vous noyer dans la technique, ni vider votre trésorerie. Cet article vous donne un plan en 3 étapes avec des outils accessibles dès 0 €. Si vous voulez d'abord avoir une vue d'ensemble de tous les outils disponibles selon votre profil, notre guide sur les <Link to="/meilleurs-outils-de-fidelisation-client" className="text-[#0c97a9] hover:underline font-semibold">outils de fidélisation client</Link> couvre le terrain complet.
            </p>

            {/* H2 - La réalité des petits commerces */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              La réalité des petits commerces face à la fidélisation
            </h2>

            <img
              src={grandeEnseigneImage}
              alt="Infographie comparant grande enseigne et petit commerce avec différences de stratégie et relation client"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un client visite un <strong className="font-semibold">commerce local</strong> 4 à 6 fois par an en moyenne lorsqu'un programme de fidélisation est en place. Sans programme, ce chiffre tombe à 1 ou 2 fois par an. L'écart est mesurable dès les 90 premiers jours.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le piège le plus fréquent : croire qu'il faut un outil sophistiqué pour produire des résultats. Un CRM à 300 € par mois a du sens pour une structure de 10 personnes avec 5 000 clients actifs. Pour un café, une boutique ou un salon, c'est une sur-ingénierie qui mobilise du temps sans produire de résultats proportionnels.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Les outils simples, bien utilisés et animés régulièrement, produisent des résultats mesurables en 60 à 90 jours.</strong> La sophistication de l'outil n'est pas un gage d'efficacité. La régularité, si.
            </p>

            {/* H2 - Étape 1 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Étape 1 : créer un canal de contact direct avec vos clients
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La fidélisation commence avant la récompense. Elle commence au moment où vous avez un moyen de recontacter un client après sa visite. Sans ce canal, chaque passage repart dans le vide : le client est venu, mais vous ne pouvez pas l'inviter à revenir.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Trois canaux sont accessibles pour un <strong className="font-semibold">petit commerce</strong>, avec des profils très différents :
            </p>

            {/* Tableau canaux */}
            <div className="overflow-x-auto my-8">
              <table className="w-full text-left text-[14px] sm:text-base font-medium text-[#495456] border-collapse">
                <thead>
                  <tr className="border-b-2 border-[#074155]">
                    <th className="py-3 pr-4 text-[#074155] font-semibold">Canal</th>
                    <th className="py-3 pr-4 text-[#074155] font-semibold">Taux d'ouverture</th>
                    <th className="py-3 pr-4 text-[#074155] font-semibold">Coût</th>
                    <th className="py-3 text-[#074155] font-semibold">Friction côté client</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4 font-medium">Email</td>
                    <td className="py-3 pr-4 font-medium">23 à 30 %</td>
                    <td className="py-3 pr-4 font-medium">Gratuit à faible</td>
                    <td className="py-3 font-medium">Faible (adresse à collecter)</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4 font-medium">SMS</td>
                    <td className="py-3 pr-4 font-medium">&gt; 90 %</td>
                    <td className="py-3 pr-4 font-medium">0,05 à 0,10 € par message</td>
                    <td className="py-3 font-medium">Faible (numéro + opt-in obligatoire)</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4 font-semibold text-[#074155]">Wallet mobile</td>
                    <td className="py-3 pr-4 font-semibold text-[#074155]">53 %</td>
                    <td className="py-3 pr-4 font-semibold text-[#074155]">Faible</td>
                    <td className="py-3 font-semibold text-[#074155]">Très faible : QR code en 30 secondes</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le <strong className="font-semibold">wallet mobile</strong> se distingue des deux autres sur un point précis : le client n'a rien à télécharger, pas de compte à créer, pas de formulaire à remplir. Il scanne un QR code affiché en caisse, confirme d'un tap, et la carte s'ajoute directement dans Apple Wallet ou Google Wallet. Vous pouvez le recontacter dès le lendemain.
            </p>

            <img
              src={walletQrCodeImage}
              alt="Schéma en 3 étapes pour ajouter une carte wallet en caisse via QR code sans téléchargement ni compte"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Pour collecter vos premiers contacts : un QR code imprimé posé à côté de la caisse suffit pour démarrer. Vous pouvez aussi l'afficher en vitrine, dans vos emails de confirmation ou sur vos réseaux sociaux.
            </p>

            {/* H2 - Étape 2 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Étape 2 : mettre en place une récompense simple et atteignable
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Une fois le canal créé, la mécanique de récompense entre en jeu. La règle fondamentale avant tout choix : <strong className="font-semibold">la première récompense doit être atteignable en 3 à 5 visites maximum</strong>. Au-delà, le client perd de vue l'objectif et abandonne le programme.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Trois mécaniques fonctionnent bien pour les petits commerces, selon la fréquence naturelle de visite de vos clients :
            </p>

            {/* Tableau mécaniques */}
            <div className="overflow-x-auto my-8">
              <table className="w-full text-left text-[14px] sm:text-base font-medium text-[#495456] border-collapse">
                <thead>
                  <tr className="border-b-2 border-[#074155]">
                    <th className="py-3 pr-4 text-[#074155] font-semibold">Type de commerce</th>
                    <th className="py-3 pr-4 text-[#074155] font-semibold">Mécanique recommandée</th>
                    <th className="py-3 text-[#074155] font-semibold">Pourquoi ça colle</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4 font-medium">Café / Boulangerie</td>
                    <td className="py-3 pr-4 font-medium">Tampon numérique (10 achats = 1 offert)</td>
                    <td className="py-3 font-medium">Achat fréquent, montant faible : la récompense arrive vite</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4 font-medium">Salon de coiffure</td>
                    <td className="py-3 pr-4 font-medium">Bon d'achat cumulatif (1 point par euro)</td>
                    <td className="py-3 font-medium">Visites espacées, panier plus élevé : les points progressent visiblement</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4 font-medium">Boutique vêtements</td>
                    <td className="py-3 pr-4 font-medium">Offre anniversaire + points cumulatifs</td>
                    <td className="py-3 font-medium">Achats saisonniers : l'anniversaire crée un retour hors saison</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4 font-medium">Restaurant</td>
                    <td className="py-3 pr-4 font-medium">Tampon + offre anniversaire</td>
                    <td className="py-3 font-medium">Mélange fréquence régulière et occasion spéciale</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4 font-medium">Coach / Formateur</td>
                    <td className="py-3 pr-4 font-medium">Parrainage + accès exclusif membres</td>
                    <td className="py-3 font-medium">La relation prime sur la récompense matérielle</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <img
              src={tamponProgressionImage}
              alt="Infographie de progression dans un programme de fidélité à tampon numérique avec récompense finale"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le choix entre ces mécaniques repose sur une seule variable : <strong className="font-semibold">la fréquence naturelle de visite</strong> de vos clients. Plus elle est élevée, plus un tampon fonctionne. Plus le panier est élevé et la visite espacée, plus un système de points cumulatifs est pertinent.
            </p>

            {/* H2 - Étape 3 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Étape 3 : animer le programme régulièrement
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un programme de fidélisation sans animation est un programme mort. La plupart des clients s'inscrivent, oublient, et ne reviennent pas. Ce n'est pas un problème d'outil : c'est un problème de contact.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le <strong className="font-semibold">minimum viable</strong> pour maintenir l'engagement : 1 message par mois à votre base de membres. Trois idées d'animation applicables en moins de 10 minutes :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>Un <strong className="font-semibold">rappel de points en attente</strong> : "Il vous reste 30 points. Encore 2 visites et votre récompense vous attend." Ce message réactive les membres qui n'ont pas utilisé leur carte depuis 3 semaines.</li>
              <li>Une <strong className="font-semibold">offre flash pour les inactifs</strong> : "Ça fait 30 jours. Un avantage exclusif vous attend cette semaine." Ce message cible les clients qui ne sont pas venus depuis un mois, avant qu'ils ne passent à autre chose.</li>
              <li>Un <strong className="font-semibold">message événement exclusif membres</strong> : "Vente privée samedi à 18h. Réservé à notre communauté." Ce message crée un sentiment d'appartenance et génère un trafic additionnel prévisible.</li>
            </ul>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La <strong className="font-semibold">notification push wallet</strong> envoie ces messages directement sur l'écran verrouillé du téléphone, sans passer par une boîte mail saturée et sans dépendre d'un algorithme de réseau social. Pour comprendre pourquoi ce canal génère un taux d'engagement deux fois supérieur à l'email, notre guide sur les <Link to="/notifications-push-wallet-fidelisation" className="text-[#0c97a9] hover:underline font-semibold">notifications push wallet : le canal de fidélisation qui cartonne en 2026</Link> détaille les chiffres et les mécanismes.
            </p>

            {/* H2 - Quel budget prévoir */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Quel budget prévoir selon votre stade de développement ?
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le budget investi dans la fidélisation doit rester proportionnel à la taille de votre base client. Voici une grille de lecture réaliste :
            </p>

            {/* Tableau budget */}
            <div className="overflow-x-auto my-8">
              <table className="w-full text-left text-[14px] sm:text-base font-medium text-[#495456] border-collapse">
                <thead>
                  <tr className="border-b-2 border-[#074155]">
                    <th className="py-3 pr-4 text-[#074155] font-semibold">Stade</th>
                    <th className="py-3 pr-4 text-[#074155] font-semibold">Budget mensuel</th>
                    <th className="py-3 pr-4 text-[#074155] font-semibold">Outils recommandés</th>
                    <th className="py-3 text-[#074155] font-semibold">Résultats attendus</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4 font-medium">Démarrage (0 à 200 clients)</td>
                    <td className="py-3 pr-4 font-medium">0 à 30 €</td>
                    <td className="py-3 pr-4 font-medium">Carte wallet gratuite ou freemium</td>
                    <td className="py-3 font-medium">Premiers retours visibles en 30 jours</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4 font-medium">Croissance (200 à 500 clients)</td>
                    <td className="py-3 pr-4 font-medium">30 à 80 €</td>
                    <td className="py-3 pr-4 font-medium">Wallet + notifications push automatisées</td>
                    <td className="py-3 font-medium">+20 % de fréquence de visite en 3 mois</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4 font-medium">Consolidation (500 clients et plus)</td>
                    <td className="py-3 pr-4 font-medium">80 à 200 €</td>
                    <td className="py-3 pr-4 font-medium">Wallet + CRM léger + email marketing</td>
                    <td className="py-3 font-medium">Programme complet avec indicateurs mesurables</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le passage d'un stade à l'autre ne se décide pas sur la durée, mais sur le volume. Quand votre base dépasse 200 clients actifs, les outils d'automatisation commencent à rentabiliser leur coût. En dessous, la gestion simple avec un outil accessible produit de meilleurs résultats qu'un système complexe mal alimenté.
            </p>

            {/* H2 - Les erreurs */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Les erreurs à ne pas faire quand on commence
            </h2>

            <img
              src={erreursImage}
              alt="Infographie des erreurs fréquentes en programme de fidélité avec correctifs recommandés pour améliorer la stratégie client"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Investir dans un CRM complet avant d'avoir 200 clients actifs.</strong> Le coût mensuel dépasse ce que la base client génère comme valeur supplémentaire à ce stade. Commencez par un outil gratuit ou freemium et montez en puissance quand le volume le justifie.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Choisir une application qui demande un téléchargement.</strong> Le taux d'adoption d'une app de fidélité dans un commerce local oscille entre 15 et 30 % : autrement dit, 70 à 85 % de vos clients ne rejoindront jamais le programme. Le wallet mobile n'a pas ce problème, précisément parce qu'il est déjà installé.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Distribuer des cartes papier sans canal de recontact.</strong> La carte papier cumule des points, mais vous ne savez pas qui la détient et vous ne pouvez pas contacter le client entre deux visites. C'est un outil de récompense, pas un outil de fidélisation.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Promettre une récompense sans la délivrer.</strong> C'est l'erreur la plus rapide pour détruire la confiance d'un client fidèle. Si la promesse ne tient pas, il ne reviendra pas et en parlera dans son entourage.
            </p>

            {/* Conclusion */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Conclusion
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Fidéliser ses clients ne demande pas un budget conséquent.</strong> Il demande une mécanique simple, un canal de contact direct et une animation régulière. Commencez par une seule chose : créer un moyen de recontacter vos clients après leur première visite. Tout le reste se construit à partir de là.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Créez votre première carte wallet sur Unotif gratuitement et contactez vos premiers membres en moins de 10 minutes.
            </p>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default OutilFidelisationPetitCommerce;
